import { AclModul, AclPermission } from '@/store/types';
import store from '@/store';
import Vue from 'vue';
import { SetStatistikDialogs } from '@/store/statistik';
import { SystemEinstellung, SystemEinstellungKey } from '@/types/Einstellung';

export type MenuDivider = {
    divider: true;
};

export type MenuItem = {
    label: string;
    icon?: string;
    drawerIcon?: string;
    minWidth?: string;
    goto?: string;
    permissions?: [AclModul, AclPermission | AclPermission[]];
    show?: () => boolean;
    action?: (component: Vue) => void;
    children?: (MenuItem | MenuDivider)[];
    rolleAnwenderReduziert? : boolean;
};

const getSystemEinstellung: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T> =
    store.getters['authentication/getSystemEinstellung'];

const setStatistik = (payload: SetStatistikDialogs): void => store.commit('statistik/setDialogs', payload);
const setSammelrechnung = (visible: boolean): void => store.commit('patient/setDialogSammelrechnung', visible);

export const menu: ReadonlyArray<MenuItem> = [
    {
        label: 'Terminkalender',
        goto: '/terminkalender',
        permissions: ['ivs', 'index'],
        drawerIcon: 'mdi-calendar-month',
        show: (): boolean => getSystemEinstellung('modul_terminkalender').isEnabled(),
    },
    {
        label: 'Impfverwaltung',
        goto: '/',
        permissions: ['ivs', 'index'],
        drawerIcon: 'mdi-home',
    },
    {
        label: 'Funktionen',
        icon: 'mdi-menu-down',
        permissions: ['ivs', 'index'],
        drawerIcon: 'mdi-cube',
        children: [
            {
                label: 'Kassaabschluss',
                goto: '/kassa',
                permissions: ['ivs', 'index'],
            },
            {
                label: 'Sammelrechnung',
                //goto: '/sammelrechnung',
                permissions: ['ivs', 'index'],
                show: (): boolean => getSystemEinstellung('muw').isEnabled(),
                action: (): void => setSammelrechnung(true),
            },
            {
                label: 'Lagerführung',
                goto: '/lager',
                permissions: ['ivs', 'index'],
            },
            {
                label: 'Impferinnerungen',
                goto: '/impferinnerungen',
                permissions: ['ivs', 'index'],
            },
            {
                label: 'Zahlungseingänge',
                goto: '/zahlungseingang',
                permissions: ['ivs', 'index'],
                rolleAnwenderReduziert: true,
            },
            {
                label: 'Zahlungserinnerungen',
                goto: '/zahlungserinnerungen',
                permissions: ['ivs', 'index'],
                rolleAnwenderReduziert: true,
            }
        ],
    },
    {
        label: 'Auswertungen',
        icon: 'mdi-menu-down',
        drawerIcon: 'mdi-text-box-search-outline',
        permissions: ['ivs', 'index'],
        minWidth: '185px',
        children: [
            {
                label: 'Offene Verrechnung',
                minWidth: '187px',
                permissions: ['ivs', 'index'],
                rolleAnwenderReduziert: true,
                action: (): void => setStatistik({ verrechnung: true }),
            },
            {
                label: 'Impfstatistik',
                permissions: ['ivs', 'index'],
                rolleAnwenderReduziert: true,
                action: (): void => setStatistik({ impfung: true }),
            },
            {
                label: 'Jahrgangs-Impfstatistik',
                permissions: ['system', 'index'],
                action: (): void => setStatistik({ jahrgang: true }),
            },
            {
                label: 'Rechnungen',
                permissions: ['ivs', 'index'],
                rolleAnwenderReduziert: true,
                action: (): void => setStatistik({ rechnung: true }),
            },
            {
                label: 'Tagesjournal',
                permissions: ['ivs', 'index'],
                rolleAnwenderReduziert: true,
                action: (): void => setStatistik({ tagesjournal: true }),
            },
            {
                label: 'Umsatzstatistik',
                permissions: ['ivs', 'index'],
                rolleAnwenderReduziert: true,
                action: (): void => setStatistik({ umsatz: true }),
            },
            {
                label: 'Offene Salden',
                permissions: ['ivs', 'index'],
                rolleAnwenderReduziert: true,
                action: (): void => setStatistik({ salden: true }),
            },
            {
                // as it's not possible to use getSystemEinstellung() for building the label,
                // we have to create two separate menu items
                label: 'Patienten mit Impfung/Untersuchung',
                permissions: ['ivs', 'index'],
                rolleAnwenderReduziert: true,
                action: (): void => setStatistik({ patientenmitimpfung: true }),
                show: (): boolean => getSystemEinstellung('muw').isEnabled()
            },
            {
                label: 'Patienten mit Impfung',
                permissions: ['ivs', 'index'],
                rolleAnwenderReduziert: true,
                action: (): void => setStatistik({ patientenmitimpfung: true }),
                show: (): boolean => !getSystemEinstellung('muw').isEnabled()
            },
            {
                label: 'Patienten mit Leistungen',
                permissions: ['system', 'index'],
                action: (): void => setStatistik({ patientenmitleistungen: true }),
            },
            {
                label: 'Patientenstatistik',
                permissions: ['admin', 'index'],
                action: (): void => setStatistik({ patientenstatistik: true }),
            },
            {
                divider: true,
                show: (): boolean => getSystemEinstellung('muw').isEnabled()
            },
            {
                label: 'Impfauswertung',
                permissions: ['ivs', 'index'],
                show: (): boolean => getSystemEinstellung('muw').isEnabled(),
                action: (): void => setStatistik({ impfauswertung: true }),
            },
            {
                label: 'Patientenstatistik (Detailliert)',
                permissions: ['ivs', 'index'],
                show: (): boolean => getSystemEinstellung('muw').isEnabled(),
                action: (): void => setStatistik({ patientenstatistik_detailiert: true }),
            },
            {
                label: 'Patientenstatistik HCW Studenten',
                permissions: ['ivs', 'index'],
                show: (): boolean => getSystemEinstellung('muw').isEnabled(),
                action: (): void => setStatistik({ patientenstatistik_studenten: true }),
            },
            {
                label: 'Preislisten',
                permissions: ['ivs', 'index'],
                show: (): boolean => getSystemEinstellung('muw').isEnabled(),
                action: (): void => setStatistik({ preislisten: true }),
            },
        ],
    },
    {
        label: 'Verwaltung',
        icon: 'mdi-menu-down',
        drawerIcon: 'mdi-file-edit',
        minWidth: '220px',
        permissions: ['admin', 'index'],
        children: [
            {
                label: 'Arbeitsplätze',
                goto: '/arbeitsplaetze',
                permissions: ['admin', 'index'],
                rolleAnwenderReduziert: true,
            },
            {
                label: 'Benutzer',
                goto: '/benutzer',
                permissions: ['admin', 'index'],
            },
            {
                label: 'Briefarten',
                goto: '/briefarten',
                permissions: ['admin', 'index'],
                rolleAnwenderReduziert: true,
                show: (): boolean => getSystemEinstellung('modul_briefe').isEnabled(),
            },
            {
                label: 'Diagnosen',
                goto: '/diagnosen',
                permissions: ['admin', 'index'],
                show: (): boolean => getSystemEinstellung('muw').isEnabled(),
            },
            {
                label: 'Dienstorte',
                goto: '/dienstorte',
                permissions: ['admin', 'index'],
                show: (): boolean => getSystemEinstellung('muw').isEnabled(),
            },
            {
                label: 'Impfaktionen',
                goto: '/impfaktionen',
                permissions: ['admin', 'index'],
                show: (): boolean => getSystemEinstellung('muw').isEnabled(),
            },
            {
                label: 'Impfempfehlungen',
                goto: '/impfempfehlungen',
                permissions: ['admin', 'index'],
                show: (): boolean => getSystemEinstellung('modul_impferinnerungen').isEnabled(),
            },
            {
                label: 'Impferinnerungstexte',
                goto: '/impferinnerungstexte',
                permissions: ['admin', 'index'],
                show: (): boolean => getSystemEinstellung('modul_impferinnerungen').isEnabled(),
            },
            {
                label: 'Impfstelle',
                permissions: ['ivs', 'index'],
                rolleAnwenderReduziert: true,
                action: (component: Vue): void => {
                    component.$emit('click:impfstelle');
                },
            },
            {
                label: 'Impfstoffe',
                goto: '/impfstoffe',
                permissions: ['admin', 'index'],
            },
            {
                label: 'Leistungen',
                goto: '/leistungen',
                permissions: ['admin', 'index'],
            },
            {
                label: 'Patientengruppen',
                goto: '/patientengruppen',
                permissions: ['system', 'index'],
                show: (): boolean => getSystemEinstellung('muw').isEnabled(),
            },
            {
                label: 'Personen',
                goto: '/personen',
                permissions: ['admin', 'index'],
                rolleAnwenderReduziert: true,
            },
            {
                label: 'Reisearten',
                goto: '/reisearten',
                permissions: ['admin', 'index'],
            },
            {
                label: 'Reiseziele',
                goto: '/reiseziele',
                permissions: ['admin', 'index'],
            },
            {
                label: 'Rezepte',
                goto: '/rezepte',
                permissions: ['admin', 'index'],
                show: (): boolean => getSystemEinstellung('modul_rezepte').isEnabled(),
            },
            {
                label: 'Studien',
                goto: '/studien',
                permissions: ['admin', 'index'],
                show: (): boolean => getSystemEinstellung('muw').isEnabled(),
            },
            {
                label: 'Terminarten',
                goto: '/terminarten',
                permissions: ['admin', 'index'],
                show: (): boolean => getSystemEinstellung('modul_terminkalender').isEnabled(),
            },
            {
                label: 'Terminplätze',
                goto: '/terminplaetze',
                permissions: ['admin', 'index'],
                show: (): boolean => getSystemEinstellung('modul_terminkalender').isEnabled(),
            },
            {
                label: 'Texte',
                goto: '/texte',
                permissions: ['admin', 'index'],
            },
            {
                label: 'Titel',
                goto: '/titel',
                permissions: ['admin', 'index'],
            },
            {
                label: 'Untersuchungen',
                goto: '/untersuchungen',
                permissions: ['admin', 'index'],
            },
            {
                label: 'Versicherungen',
                goto: '/versicherungen',
                permissions: ['admin', 'index'],
            },
        ],
    },
    {
        label: 'Admin',
        icon: 'mdi-menu-down',
        drawerIcon: 'mdi-book',
        minWidth: '220px',
        permissions: ['admin', 'index'],
        children: [
            {
                label: 'ELGA Übertragung',
                goto: '/elga',
                permissions: ['admin', 'index'],
            },
            {
                label: 'Termin Anforderungen',
                goto: '/anforderungen',
                permissions: ['ivs', 'index'],
                show: (): boolean => getSystemEinstellung('system_termine').isEnabled() && getSystemEinstellung('modul_terminkalender').isEnabled(),
            },
            {
                label: 'Termin Anmeldungen',
                goto: '/terminanmeldungen',
                permissions: ['admin', 'index'],
                show: (): boolean => getSystemEinstellung('system_termine').isEnabled() && getSystemEinstellung('modul_terminkalender').isEnabled(),
            },
            {
                label: 'Logbuch',
                goto: '/logbuch',
                permissions: ['admin', 'index'],
            },
            {
                label: 'Logbuch Emails',
                goto: '/emails',
                permissions: ['admin', 'index'],
            },
            {
                divider: true,
            },
            {
                label: 'APCU',
                permissions: ['system', 'index'],
                action: (component: Vue): void => {
                    component.$emit('click:apcu');
                },
            },
            {
                label: 'Einstellungen',
                goto: '/einstellungen',
                permissions: ['system', 'index'],
            },
            {
                label: 'Logs',
                goto: '/jawa/logbuch',
                permissions: ['system', 'index'],
            },
            {
                label: 'Systemdaten',
                permissions: ['admin', 'index'],
                action: (component: Vue): void => {
                    component.$emit('click:systemdaten');
                },
            },
            {
                label: 'Terminologien',
                permissions: ['system', 'index'],
                action: (component: Vue): void => {
                    component.$emit('click:terminologien');
                },
            },
        ],
    },
];
